import { UserNotification, NotificationPriority, NotificationType } from "@/lib/api/user_notification";

/**
 * Utility functions for user notification management
 */

// Priority level mappings
export const PRIORITY_LABELS: Record<number, string> = {
  [NotificationPriority.Background]: "Background",
  [NotificationPriority.Low]: "Low",
  [NotificationPriority.Medium]: "Medium",
  [NotificationPriority.High]: "High",
  [NotificationPriority.Urgent]: "Urgent",
};

// Type display mappings
export const TYPE_LABELS: Record<string, string> = {
  [NotificationType.System]: "System",
  [NotificationType.RiskCustomerActivities]: "Notes/To-Dos",
};

// Sort orders for priorities (higher number = higher priority)
export const PRIORITY_SORT_ORDER: Record<number, number> = {
  [NotificationPriority.Urgent]: 5,
  [NotificationPriority.High]: 4,
  [NotificationPriority.Medium]: 3,
  [NotificationPriority.Low]: 2,
  [NotificationPriority.Background]: 1,
};

/**
 * Get human-readable label for notification priority
 */
export function getPriorityLabel(priority: number): string {
  return PRIORITY_LABELS[priority] || `Priority ${priority}`;
}

/**
 * Get human-readable label for notification type
 */
export function getTypeLabel(type: string): string {
  return TYPE_LABELS[type] || type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
}

/**
 * Check if notification is high priority (High or Urgent)
 */
export function isHighPriority(notification: UserNotification): boolean {
  return notification.notification_priority >= NotificationPriority.High;
}

/**
 * Check if notification is actionable (has a link)
 */
export function isActionable(notification: UserNotification): boolean {
  return Boolean(notification.link);
}

/**
 * Get notification age in days
 */
export function getNotificationAge(notification: UserNotification): number {
  const now = new Date();
  const created = new Date(notification.created_at);
  const diffTime = Math.abs(now.getTime() - created.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Check if notification is recent (created within last 24 hours)
 */
export function isRecentNotification(notification: UserNotification): boolean {
  return getNotificationAge(notification) <= 1;
}

/**
 * Sort notifications by priority (highest first), then by creation date (newest first)
 */
export function sortNotificationsByPriority(notifications: UserNotification[]): UserNotification[] {
  return [...notifications].sort((a, b) => {
    // First sort by priority (highest first)
    const priorityDiff = PRIORITY_SORT_ORDER[b.notification_priority] - PRIORITY_SORT_ORDER[a.notification_priority];
    if (priorityDiff !== 0) return priorityDiff;

    // Then sort by creation date (newest first)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}

/**
 * Group notifications by type
 */
export function groupNotificationsByType(notifications: UserNotification[]): Record<string, UserNotification[]> {
  return notifications.reduce((groups, notification) => {
    const type = notification.notification_type;
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(notification);
    return groups;
  }, {} as Record<string, UserNotification[]>);
}

/**
 * Group notifications by priority
 */
export function groupNotificationsByPriority(notifications: UserNotification[]): Record<number, UserNotification[]> {
  return notifications.reduce((groups, notification) => {
    const priority = notification.notification_priority;
    if (!groups[priority]) {
      groups[priority] = [];
    }
    groups[priority].push(notification);
    return groups;
  }, {} as Record<number, UserNotification[]>);
}

/**
 * Filter notifications by read status
 */
export function filterByReadStatus(notifications: UserNotification[], unreadOnly: boolean = false): UserNotification[] {
  if (!unreadOnly) return notifications;
  return notifications.filter(notification => !notification.is_read);
}

/**
 * Filter notifications by priority levels
 */
export function filterByPriorities(notifications: UserNotification[], priorities: number[]): UserNotification[] {
  if (priorities.length === 0) return notifications;
  return notifications.filter(notification => priorities.includes(notification.notification_priority));
}

/**
 * Filter notifications by types
 */
export function filterByTypes(notifications: UserNotification[], types: string[]): UserNotification[] {
  if (types.length === 0) return notifications;
  return notifications.filter(notification => types.includes(notification.notification_type));
}

/**
 * Get summary statistics for a list of notifications
 */
export interface NotificationStats {
  total: number;
  unread: number;
  byPriority: Record<number, number>;
  byType: Record<string, number>;
  recent: number;
  actionable: number;
}

export function getNotificationStats(notifications: UserNotification[]): NotificationStats {
  const stats: NotificationStats = {
    total: notifications.length,
    unread: 0,
    byPriority: {},
    byType: {},
    recent: 0,
    actionable: 0,
  };

  notifications.forEach(notification => {
    // Count unread
    if (!notification.is_read) {
      stats.unread++;
    }

    // Count by priority
    const priority = notification.notification_priority;
    stats.byPriority[priority] = (stats.byPriority[priority] || 0) + 1;

    // Count by type
    const type = notification.notification_type;
    stats.byType[type] = (stats.byType[type] || 0) + 1;

    // Count recent
    if (isRecentNotification(notification)) {
      stats.recent++;
    }

    // Count actionable
    if (isActionable(notification)) {
      stats.actionable++;
    }
  });

  return stats;
}

/**
 * Generate a summary string for notification filters
 */
export function getFilterSummary(filters: {
  priorities?: number[];
  types?: string[];
  groups?: string[];
  filter_to?: ("UNREAD")[];
}): string {
  const parts: string[] = [];

  if (filters.priorities && filters.priorities.length > 0) {
    const priorityLabels = filters.priorities.map(p => getPriorityLabel(p));
    parts.push(`Priorities: ${priorityLabels.join(", ")}`);
  }

  if (filters.types && filters.types.length > 0) {
    const typeLabels = filters.types.map(t => getTypeLabel(t));
    parts.push(`Types: ${typeLabels.join(", ")}`);
  }

  if (filters.groups && filters.groups.length > 0) {
    parts.push(`Groups: ${filters.groups.join(", ")}`);
  }

  if (filters.filter_to && filters.filter_to.includes("UNREAD")) {
    parts.push("Unread only");
  }

  return parts.length > 0 ? parts.join(" • ") : "No filters applied";
}

/**
 * Truncate notification message to specified length
 */
export function truncateMessage(message: string, maxLength: number = 100): string {
  if (message.length <= maxLength) return message;
  return message.substring(0, maxLength).replace(/\s+\S*$/, '') + '...';
}

export interface NotificationActionResult {
  success: boolean;
  error?: string;
}
